import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronRight,
  LogOut,
  Package,
  Heart,
  AlertTriangle,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";

export default function AccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    toast.success("Sesión cerrada");
    navigate("/", { replace: true });
  };

  // Fase A · Borrar cuenta — estado del modal de confirmación.
  const [openDelete, setOpenDelete] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const openDeleteModal = () => {
    setPassword("");
    setConfirmChecked(false);
    setDeleteError(null);
    setOpenDelete(true);
  };

  const canSubmitDelete = password.trim().length > 0 && confirmChecked && !deleting;

  const submitDelete = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      // Pasamos la zona IANA del navegador para que el email ponga la hora
      // de borrado en la zona del cliente (y no en UTC crudo).
      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid";
      await api.delete("/users/me", {
        data: { password, confirm: true, timezone },
      });
      toast.success("Cuenta eliminada. Te enviaremos un email de confirmación.");
      setOpenDelete(false);
      // Fuerza logout local y redirect a home tras 2s para que el usuario
      // alcance a leer el toast.
      logout();
      setTimeout(() => navigate("/", { replace: true }), 2000);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 403) {
        setDeleteError("No puedes eliminar una cuenta admin desde aquí.");
      } else if (status === 400 && typeof detail === "string") {
        setDeleteError(detail);
      } else {
        setDeleteError("No se pudo eliminar la cuenta. Inténtalo de nuevo.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div
      data-testid="account-page"
      className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10"
    >
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Mi cuenta</h1>
      <div className="mt-2 text-neutral-600">
        <span className="font-semibold" data-testid="account-name">{user.name}</span> ·{" "}
        <span data-testid="account-email">{user.email}</span>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <Link
          to="/mis-pedidos"
          data-testid="account-to-orders"
          className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#00953B] hover:shadow-md transition-all flex items-center gap-4"
        >
          <div className="h-12 w-12 rounded-xl bg-[#F1FAF3] grid place-items-center">
            <Package className="h-6 w-6 text-[#00953B]" />
          </div>
          <div className="flex-1">
            <div className="font-semibold">Mis pedidos</div>
            <div className="text-sm text-neutral-500">
              Historial y detalles de tus compras
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-neutral-400 group-hover:text-[#00953B]" />
        </Link>
        <Link
          to="/mis-habituales"
          data-testid="account-to-favorites"
          className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#00953B] hover:shadow-md transition-all flex items-center gap-4"
        >
          <div className="h-12 w-12 rounded-xl bg-[#F1FAF3] grid place-items-center">
            <Heart className="h-6 w-6 text-[#00953B]" />
          </div>
          <div className="flex-1">
            <div className="font-semibold">Mis habituales</div>
            <div className="text-sm text-neutral-500">
              Los productos que compras a menudo
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-neutral-400 group-hover:text-[#00953B]" />
        </Link>
      </div>

      <div className="mt-10">
        <Button
          variant="outline"
          onClick={doLogout}
          data-testid="account-logout-btn"
          className="rounded-full border-neutral-200 gap-2"
        >
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </Button>
      </div>

      {/* Fase A · Zona peligrosa — borrar cuenta. Oculta para admins. */}
      {!isAdmin && (
        <section
          data-testid="account-danger-zone"
          className="mt-12 rounded-2xl border-2 border-red-200 bg-red-50/50 p-6"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 rounded-2xl bg-white border border-red-200 p-3">
              <AlertTriangle
                className="h-6 w-6 text-red-600"
                strokeWidth={2.25}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-black tracking-tight text-red-900">
                Zona peligrosa
              </h2>
              <p className="mt-1 text-sm text-red-800/80">
                Eliminar mi cuenta — esta acción es <b>irreversible</b> y
                borrará todos tus datos personales. Tus pedidos pasados se
                mantendrán anonimizados para la contabilidad de MercaGo.
              </p>
              <Button
                variant="outline"
                onClick={openDeleteModal}
                data-testid="account-delete-btn"
                className="mt-4 rounded-full border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Eliminar mi cuenta
              </Button>
              <p className="mt-3 text-xs text-red-800/70">
                También puedes gestionar la eliminación desde{" "}
                <Link
                  to="/eliminar-cuenta"
                  data-testid="account-delete-shareable-link"
                  className="font-semibold underline hover:text-red-900"
                >
                  este enlace
                </Link>
                .
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Modal de confirmación en 2 pasos */}
      <Dialog open={openDelete} onOpenChange={(v) => !deleting && setOpenDelete(v)}>
        <DialogContent
          data-testid="account-delete-modal"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-red-100 grid place-items-center mb-2">
              <AlertTriangle
                className="h-6 w-6 text-red-600"
                strokeWidth={2.25}
              />
            </div>
            <DialogTitle className="text-center">
              ¿Eliminar tu cuenta?
            </DialogTitle>
            <DialogDescription className="text-center">
              Esta acción es <b>irreversible</b>. Introduce tu contraseña para
              confirmar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="delete-password" className="text-sm">
                Contraseña actual
              </Label>
              <Input
                id="delete-password"
                type="password"
                value={password}
                onChange={(e) => {
                  setDeleteError(null);
                  setPassword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSubmitDelete) {
                    e.preventDefault();
                    submitDelete();
                  }
                }}
                data-testid="account-delete-password-input"
                autoComplete="current-password"
                placeholder="Tu contraseña"
                className="mt-1 h-11"
              />
            </div>

            <label
              htmlFor="delete-confirm"
              className="flex items-start gap-3 rounded-xl border border-red-200 bg-white p-3 cursor-pointer"
            >
              <Checkbox
                id="delete-confirm"
                checked={confirmChecked}
                onCheckedChange={(v) => setConfirmChecked(!!v)}
                data-testid="account-delete-confirm-checkbox"
                className="mt-0.5"
              />
              <span className="text-sm text-neutral-700 leading-snug">
                Entiendo que esta acción es irreversible y perderé acceso a
                mis pedidos pasados.
              </span>
            </label>

            {deleteError && (
              <div
                data-testid="account-delete-error"
                className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
              >
                {deleteError}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              disabled={deleting}
              onClick={() => setOpenDelete(false)}
              data-testid="account-delete-cancel-btn"
              className="rounded-full"
            >
              Cancelar
            </Button>
            <Button
              disabled={!canSubmitDelete}
              onClick={submitDelete}
              data-testid="account-delete-confirm-btn"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Eliminando…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Eliminar definitivamente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
